import { useEffect, useRef, useState } from 'react'
import { appHref, frontPageTiles, useMyApps } from '@omni/front-door'
import { Check, ChevronsUpDown, Home, LayoutGrid } from 'lucide-react'
import { productName } from '../lib/config'
import { FRONT_DOOR_APP, FRONT_PAGE_URL, frontDoor } from './config'
import './front-door.css'

// The app switcher in the shell chrome (doc section 8): the front page and
// the person's other apps, as plain links across subdomains (one sign-in, so
// no second login). Front-door builds only (Chrome.tsx, behind __FRONT_DOOR__).

export function AppSwitcher({ collapsed = false }: { collapsed?: boolean }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const apps = useMyApps()
  const tiles = apps.status === 'ready' ? frontPageTiles(apps.data) : []
  const current = FRONT_DOOR_APP ? tiles.find((t) => t.slug === FRONT_DOOR_APP)?.name ?? 'This app' : 'Front page'

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className={`fd-switch ${collapsed ? 'fd-switch-collapsed' : ''}`}>
      <button
        type="button"
        className="fd-switch-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Switch app"
        aria-label={`Switch app (now: ${current})`}
        onClick={() => setOpen((v) => !v)}
      >
        <LayoutGrid size={16} className="shrink-0" />
        {!collapsed && (
          <>
            <span className="fd-switch-now">
              <span className="fd-switch-kicker">App</span>
              <span className="truncate">{current}</span>
            </span>
            <ChevronsUpDown size={14} className="ml-auto shrink-0 opacity-60" />
          </>
        )}
      </button>
      {open && (
        <div className="fd-switch-menu" role="menu" aria-label={`${productName} apps`}>
          <a role="menuitem" className="fd-switch-item" href={FRONT_PAGE_URL}>
            <Home size={15} />
            <span className="fd-switch-item-name">Front page</span>
            {!FRONT_DOOR_APP && <Check size={14} className="fd-switch-here" aria-label="You are here" />}
          </a>
          {apps.status === 'loading' && <p className="fd-switch-note">Loading your apps…</p>}
          {apps.status === 'error' && <p className="fd-switch-note">Your apps could not load just now.</p>}
          {apps.status === 'ready' && tiles.length === 0 && <p className="fd-switch-note">No apps yet.</p>}
          {tiles.map((t) => {
            const href = appHref(t, frontDoor.domain)
            const here = t.slug === FRONT_DOOR_APP
            if (t.status === 'building' || !href) {
              return (
                <span key={t.slug} role="menuitem" aria-disabled="true" className="fd-switch-item fd-switch-item-off">
                  <span className="fd-switch-dot" aria-hidden>{t.name.charAt(0)}</span>
                  <span className="fd-switch-item-name">{t.name}</span>
                  <span className="fd-switch-soon">Coming soon</span>
                </span>
              )
            }
            return (
              <a key={t.slug} role="menuitem" className="fd-switch-item" href={href} aria-current={here ? 'page' : undefined}>
                <span className="fd-switch-dot" aria-hidden>{t.name.charAt(0)}</span>
                <span className="fd-switch-item-name">{t.name}</span>
                {here && <Check size={14} className="fd-switch-here" aria-label="You are here" />}
              </a>
            )
          })}
        </div>
      )}
    </div>
  )
}
